import React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Paper from "@mui/material/Paper";
import { motion } from "framer-motion";

// Import your logo assets
import figLogo from "../../assets/logos/fig_CROP.png";
import swanSolutionsLogo from "../../assets/logos/swansolutions_logo.png";
import swanAiLabsLogo from "../../assets/logos/swan_ai_labs_logo.png";

// Import the improved "Software Built for K12" section
import WhatWeBuild from "./WhatWeBuild";

const solutionCategories = [
    {
        title: "Finance & Accounting",
        description: "USDA-compliant financial management for K12 nutrition programs.",
        logo: figLogo,
        link: "/solutions#finance",
    },
    {
        title: "Food & Nutrition",
        description: "Menu planning, POS, and nutrition compliance for school meals.",
        logo: figLogo,
        link: "/solutions#nutrition",
    },
    {
        title: "Procurement & Audit",
        description: "AI-powered bid analytics, invoice review, and audit tools.",
        logo: swanSolutionsLogo,
        link: "/solutions#procurement",
    },
    {
        title: "CRM & Partnerships",
        description: "District and vendor relationship management.",
        logo: swanSolutionsLogo,
        link: "/solutions#crm",
    },
    {
        title: "AI & Custom Dev",
        description: "RAG, chatbots, analytics, and fully custom enterprise software.",
        logo: swanAiLabsLogo,
        link: "/solutions#ai",
    },
    {
        title: "AIOS Platform",
        description: "Our Agentic OS connecting 40+ databases with AI intelligence.",
        logo: swanAiLabsLogo,
        link: "/aios",
    },
];

export default function Home() {
    // Haptic animation config for motion
    const haptic = {
        whileTap: { scale: 0.96 },
        whileHover: { scale: 1.03 },
        transition: { type: "spring", stiffness: 330, damping: 16 },
    };

    return (
        <Box sx={{ width: "100%", bgcolor: "background.default" }}>
            {/* Hero Section */}
            <Box
                component={motion.section}
                initial={{ opacity: 0, y: -64 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                sx={{
                    width: "100vw",
                    left: "50%",
                    right: "50%",
                    marginLeft: "-50vw",
                    marginRight: "-50vw",
                    px: 0,
                    py: { xs: 10, md: 16 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "#fff",
                    background:
                        "linear-gradient(120deg, #0b223e 0%, #2269a6 45%, #21dbb8 100%)",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    minHeight: { xs: "70vh", md: "90vh" },
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0 6px 40px 0 rgba(30,90,150,0.13)",
                    mb: 10,
                }}
            >
                <Box
                    component="img"
                    src={swanSolutionsLogo}
                    alt="Swan Solutions Logo"
                    sx={{
                        height: { xs: 80, md: 140 },
                        mb: 2,
                        filter: "drop-shadow(0 4px 24px rgba(0,0,0,0.15))",
                    }}
                />
                <Typography
                    variant="h1"
                    gutterBottom
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: "2.5rem", md: "4rem" },
                        letterSpacing: "-0.04em",
                        mb: 1,
                        textShadow: "0 3px 16px rgba(25,60,110,0.15)",
                    }}
                >
                    Swan Solutions
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        mb: 4,
                        fontWeight: 700,
                        fontSize: { xs: "1.18rem", md: "1.65rem" },
                        maxWidth: 800,
                        mx: "auto",
                        textShadow: "0 2px 14px rgba(20,92,140,0.13)",
                    }}
                >
                    K12 Technology Partner &mdash; Finance &bull; Nutrition &bull; AI &bull; Custom Software
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        maxWidth: 700,
                        mx: "auto",
                        mb: 4,
                        fontWeight: 400,
                        fontSize: { xs: "1rem", md: "1.17rem" },
                        color: "rgba(255,255,255,0.97)",
                    }}
                >
                    We build modern, practical software for school districts and for companies that serve K12 operations nationwide.
                </Typography>
                <Button
                    component={motion.a}
                    href="/about"
                    size="large"
                    variant="contained"
                    color="secondary"
                    {...haptic}
                    sx={{
                        fontSize: "1.08rem",
                        fontWeight: 700,
                        py: 1.5,
                        px: 4,
                        borderRadius: 99,
                        boxShadow: "0 3px 14px rgba(44,153,200,0.12)",
                        mt: 1,
                    }}
                >
                    Learn More About Us
                </Button>
            </Box>

            {/* Who We Are */}
            <Container
                maxWidth="lg"
                component={motion.section}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.7 }}
                sx={{ textAlign: "center", mb: 8 }}
            >
                <Typography
                    variant="h3"
                    gutterBottom
                    sx={{ fontWeight: 800, fontSize: { xs: "2.2rem", md: "2.8rem" } }}
                >
                    Who We Are
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        maxWidth: 820,
                        mx: "auto",
                        mb: 3,
                        fontSize: { xs: "1.07rem", md: "1.15rem" },
                        fontWeight: 400,
                    }}
                >
                    Swan Solutions is a K12 software company with deep expertise in school nutrition, operations, and compliance. We deliver platforms and integrations that simplify complex processes, automate reporting, and empower leaders to make strategic, data-driven decisions.
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        maxWidth: 820,
                        mx: "auto",
                        fontSize: { xs: "1.07rem", md: "1.13rem" },
                        fontWeight: 400,
                    }}
                >
                    Our clients include districts of every size, plus vendors, service providers, and EdTech partners. From flagship products like FIG Finance to complete custom builds, we modernize, streamline, and help you better serve your communities.
                </Typography>
            </Container>

            {/* Product Categories Overview */}
            <Container maxWidth="lg" sx={{ mb: 10 }}>
                <Typography
                    variant="h3"
                    align="center"
                    gutterBottom
                    sx={{
                        fontWeight: 800,
                        mb: 2,
                        fontSize: { xs: "1.8rem", md: "2.5rem" },
                    }}
                >
                    What We Build
                </Typography>
                <Typography
                    align="center"
                    sx={{
                        color: "text.secondary",
                        fontSize: "1.13rem",
                        mb: 5,
                        maxWidth: 700,
                        mx: "auto",
                    }}
                >
                    Platforms and tools for every part of K12 operations.
                </Typography>
                <Grid container spacing={3} justifyContent="center">
                    {solutionCategories.map((cat, i) => (
                        <Grid item xs={12} sm={6} md={4} key={cat.title}>
                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.06 * i, duration: 0.45 }}
                                whileHover={{ scale: 1.03 }}
                                style={{ height: "100%" }}
                            >
                                <Paper
                                    component="a"
                                    href={cat.link}
                                    elevation={0}
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        textAlign: "center",
                                        p: 3,
                                        height: "100%",
                                        borderRadius: 4,
                                        textDecoration: "none",
                                        color: "inherit",
                                        bgcolor: "#fff",
                                        boxShadow: "0 2px 20px rgba(20,76,100,0.07)",
                                        transition: "box-shadow 0.2s",
                                        "&:hover": {
                                            boxShadow: "0 6px 32px rgba(33,219,184,0.15)",
                                        },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={cat.logo}
                                        alt={cat.title}
                                        sx={{
                                            height: 48,
                                            width: 48,
                                            objectFit: "contain",
                                            borderRadius: 2,
                                            bgcolor: "#e7f5f6",
                                            mb: 2,
                                        }}
                                    />
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            color: "primary.main",
                                            mb: 0.5,
                                            fontSize: "1.08rem",
                                        }}
                                    >
                                        {cat.title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "text.secondary", fontSize: "0.97rem" }}
                                    >
                                        {cat.description}
                                    </Typography>
                                </Paper>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Container>

            {/* --- Improved "Software Built for K12" Section --- */}
            <WhatWeBuild />

            {/* AIOS Platform Card */}
            <Container maxWidth="md" sx={{ mb: 10 }}>
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                >
                    <Card
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            alignItems: "center",
                            p: { xs: 3, md: 4 },
                            borderRadius: 4,
                            boxShadow: 6,
                            background: "linear-gradient(135deg, #003366 0%, #00CFFE 100%)",
                            color: "#fff",
                        }}
                    >
                        <Box
                            component="img"
                            src={swanAiLabsLogo}
                            alt="AIOS"
                            sx={{
                                width: { xs: 100, md: 150 },
                                height: "auto",
                                mb: { xs: 2, md: 0 },
                                mr: { md: 4 },
                                borderRadius: 2,
                                bgcolor: "rgba(255,255,255,0.12)",
                                p: 1.5,
                                filter: "brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0,0,0,0.2))",
                            }}
                        />
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 900,
                                    mb: 1,
                                    letterSpacing: "-0.01em",
                                }}
                            >
                                AIOS — Agentic Operating System
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2, color: "rgba(255,255,255,0.92)" }}>
                                The AI brain connecting every Swan platform. AIOS manages 40+ databases, coordinates autonomous agents, and delivers federated intelligence across our entire ecosystem — making every system smarter.
                            </Typography>
                            <Button
                                component={motion.a}
                                href="/aios"
                                variant="contained"
                                size="large"
                                {...haptic}
                                sx={{
                                    fontWeight: 700,
                                    borderRadius: 99,
                                    px: 4,
                                    fontSize: "1.05rem",
                                    bgcolor: "#fff",
                                    color: "#003366",
                                    "&:hover": {
                                        bgcolor: "rgba(255,255,255,0.9)",
                                    },
                                }}
                            >
                                Discover AIOS
                            </Button>
                        </Box>
                    </Card>
                </motion.div>
            </Container>
        </Box>
    );
}
